import { useState, type CSSProperties, type FormEvent, type ReactElement } from "react";
import { createRoot } from "react-dom/client";
import {
  ACCENT,
  ACCENT_HI,
  ACCENT_INK,
  BAD,
  FOCUS_RING,
  LINE_STRONG,
  RADIUS,
  RADIUS_SM,
  SHADOW_MODAL,
  TEXT_DIM,
  UI_FAMILY,
} from "./ui/theme.js";

export interface AuthAccount {
  username: string;
  name: string;
}

export type AuthBootstrap =
  | { kind: "direct" }
  | { kind: "account"; account: AuthAccount };

/**
 * Detects a direct server, resumes an HttpOnly-cookie session, or shows the
 * credential form until login/registration succeeds.
 */
export async function authenticateForGame(serverUrl: string): Promise<AuthBootstrap> {
  const base = httpBase(serverUrl);
  try {
    const response = await fetch(`${base}/auth/session`, {
      credentials: "include",
      cache: "no-store",
    });
    if (response.status === 404) return { kind: "direct" };
    if (response.ok) {
      const account = await accountFrom(response);
      if (account !== null) return { kind: "account", account };
    }
    if (response.status !== 401) {
      return showAuthScreen(base, `Server responded with ${response.status}.`);
    }
    return showAuthScreen(base);
  } catch {
    return showAuthScreen(base, "Game server is unreachable.");
  }
}

export async function logoutFromGame(serverUrl: string): Promise<void> {
  await fetch(`${httpBase(serverUrl)}/auth/logout`, {
    method: "POST",
    credentials: "include",
  });
}

function showAuthScreen(base: string, initialError = ""): Promise<AuthBootstrap> {
  const container = document.getElementById("ui");
  if (container === null) return Promise.reject(new Error("missing #ui auth container"));
  const scrim = document.getElementById("scrim");
  if (scrim !== null) scrim.hidden = true;
  const root = createRoot(container);
  return new Promise((resolve) => {
    root.render(
      <AuthScreen
        base={base}
        initialError={initialError}
        onAuthenticated={(account) => {
          root.unmount();
          if (scrim !== null) scrim.hidden = false;
          resolve({ kind: "account", account });
        }}
      />,
    );
  });
}

function AuthScreen(props: {
  base: string;
  initialError: string;
  onAuthenticated: (account: AuthAccount) => void;
}): ReactElement {
  const [mode, setMode] = useState<"login" | "register">("login");
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(props.initialError);

  const submit = (event: FormEvent<HTMLFormElement>): void => {
    event.preventDefault();
    if (busy) return;
    if (mode === "register" && password !== confirm) {
      setError("The passwords do not match.");
      return;
    }
    setBusy(true);
    setError("");
    void fetch(`${props.base}/auth/${mode}`, {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ username, password }),
    }).then(
      async (response) => {
        const payload = await jsonObject(response);
        if (!response.ok) {
          const message = payload?.["message"];
          setError(
            typeof message === "string"
              ? message
              : response.status === 404
                ? "This server does not support accounts."
                : "Sign-in failed.",
          );
          setBusy(false);
          return;
        }
        const account = accountIn(payload);
        if (account === null) {
          setError("Invalid server response.");
          setBusy(false);
          return;
        }
        props.onAuthenticated(account);
      },
      () => {
        setError("Game server is unreachable.");
        setBusy(false);
      },
    );
  };

  return (
    <div className="app-backdrop mm-auth" style={styles.backdrop}>
      {/* This screen mounts BEFORE the HUD, so the menu's stylesheet is not on
          the page yet: the two states inline styles cannot express come with
          it. */}
      <style>{AUTH_CSS}</style>
      <form style={styles.panel} onSubmit={submit}>
        <div style={styles.mark}>EfR</div>
        <div style={styles.eyebrow}>ESCAPE FROM RIETZ</div>
        <h1 style={styles.title}>{mode === "login" ? "Sign in" : "Create account"}</h1>
        <p style={styles.copy}>
          Your gear, stash, money, and quests are saved securely to this account.
        </p>

        <label style={styles.label}>
          Username
          <input
            autoFocus
            autoComplete="username"
            minLength={3}
            maxLength={24}
            pattern="[A-Za-z0-9_.-]+"
            required
            value={username}
            onChange={(event) => setUsername(event.currentTarget.value)}
            style={styles.input}
          />
        </label>
        <label style={styles.label}>
          Password
          <input
            type="password"
            autoComplete={mode === "login" ? "current-password" : "new-password"}
            minLength={8}
            maxLength={128}
            required
            value={password}
            onChange={(event) => setPassword(event.currentTarget.value)}
            style={styles.input}
          />
        </label>
        {mode === "register" ? (
          <label style={styles.label}>
            Confirm password
            <input
              type="password"
              autoComplete="new-password"
              minLength={8}
              maxLength={128}
              required
              value={confirm}
              onChange={(event) => setConfirm(event.currentTarget.value)}
              style={styles.input}
            />
          </label>
        ) : null}

        <div role="alert" style={styles.error}>
          {error}
        </div>
        <button
          type="submit"
          disabled={busy}
          style={busy ? { ...styles.primary, opacity: 0.55, cursor: "default" } : styles.primary}
        >
          {busy ? "Please wait…" : mode === "login" ? "Sign in" : "Register"}
        </button>
        <button
          type="button"
          disabled={busy}
          style={styles.switch}
          onClick={() => {
            setMode(mode === "login" ? "register" : "login");
            setError("");
            setConfirm("");
          }}
        >
          {mode === "login" ? "No account yet? Register" : "Already registered? Sign in"}
        </button>
      </form>
    </div>
  );
}

export function httpBase(serverUrl: string): string {
  const url = new URL(serverUrl, window.location.href);
  url.protocol = url.protocol === "wss:" ? "https:" : "http:";
  url.pathname = "";
  url.search = "";
  url.hash = "";
  return url.toString().replace(/\/$/, "");
}

async function accountFrom(response: Response): Promise<AuthAccount | null> {
  return accountIn(await jsonObject(response));
}

async function jsonObject(response: Response): Promise<Record<string, unknown> | null> {
  try {
    const value: unknown = await response.json();
    return typeof value === "object" && value !== null && !Array.isArray(value)
      ? (value as Record<string, unknown>)
      : null;
  } catch {
    return null;
  }
}

function accountIn(payload: Record<string, unknown> | null): AuthAccount | null {
  const account = payload?.["account"];
  if (typeof account !== "object" || account === null || Array.isArray(account)) return null;
  const username = (account as Record<string, unknown>)["username"];
  const name = (account as Record<string, unknown>)["name"];
  return typeof username === "string" && typeof name === "string" ? { username, name } : null;
}

/** The two states an inline style cannot carry. Scoped to `.mm-auth`. */
const AUTH_CSS = `
.mm-auth input:focus { outline: none; border-color: rgba(214, 176, 108, .6); }
.mm-auth button:focus-visible { outline: none; box-shadow: ${FOCUS_RING}; }
`;

const styles = {
  backdrop: {
    position: "absolute",
    inset: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    pointerEvents: "auto",
    color: "#e6ecf2",
    font: `13px/1.45 ${UI_FAMILY}`,
  },
  panel: {
    width: "min(400px, calc(100vw - 40px))",
    boxSizing: "border-box",
    padding: "30px 32px 26px",
    border: `1px solid ${LINE_STRONG}`,
    borderRadius: 14,
    background: "rgb(19, 22, 26)",
    boxShadow: SHADOW_MODAL,
    display: "flex",
    flexDirection: "column",
    gap: 12,
  },
  mark: {
    width: 28,
    height: 28,
    borderRadius: 8,
    display: "grid",
    placeItems: "center",
    color: ACCENT_INK,
    background: `linear-gradient(160deg, ${ACCENT_HI}, ${ACCENT})`,
    boxShadow: "0 4px 14px rgba(214, 176, 108, .28)",
    font: `800 13px/1 ${UI_FAMILY}`,
    letterSpacing: "-0.02em",
  },
  eyebrow: {
    color: TEXT_DIM,
    fontSize: 10.5,
    fontWeight: 600,
    letterSpacing: "0.14em",
    textTransform: "uppercase",
  },
  title: { margin: 0, fontSize: 26, fontWeight: 700, letterSpacing: "-0.01em" },
  copy: { margin: "-4px 0 6px", color: TEXT_DIM, fontSize: 12, lineHeight: 1.5 },
  label: {
    display: "flex",
    flexDirection: "column",
    gap: 6,
    color: TEXT_DIM,
    fontSize: 11.5,
    fontWeight: 600,
    letterSpacing: "0.04em",
  },
  input: {
    boxSizing: "border-box",
    width: "100%",
    height: 38,
    padding: "0 11px",
    border: `1px solid ${LINE_STRONG}`,
    borderRadius: RADIUS_SM,
    outline: "none",
    color: "#f4f8fc",
    background: "rgba(0, 0, 0, .32)",
    font: `13.5px/1.2 ${UI_FAMILY}`,
  },
  error: { minHeight: 18, color: BAD, fontSize: 12 },
  primary: {
    height: 40,
    marginTop: 2,
    border: 0,
    borderRadius: RADIUS,
    color: ACCENT_INK,
    background: `linear-gradient(180deg, ${ACCENT_HI}, ${ACCENT})`,
    boxShadow: "0 6px 18px rgba(214, 176, 108, .22), inset 0 1px 0 rgba(255, 255, 255, .35)",
    font: `700 13.5px/1 ${UI_FAMILY}`,
    letterSpacing: "0.02em",
    cursor: "pointer",
  },
  switch: {
    alignSelf: "center",
    border: 0,
    borderRadius: RADIUS_SM,
    padding: 4,
    color: TEXT_DIM,
    background: "transparent",
    font: `12px/1.2 ${UI_FAMILY}`,
    cursor: "pointer",
  },
} satisfies Record<string, CSSProperties>;
